'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { GridMatch, SharedGrid } from '@/types/tournament.model';
import { cn } from '@/lib/utils';
import {
  buildGrid,
  effectiveCell,
  tournamentOrderOf,
  TOURNAMENT_DOT_COLORS,
  type GridSlotRef,
  type GridViewMode,
  type PlaceholderLabelMap,
  type TournamentBadge,
} from './grid-model';
import { GridMatrix } from './GridMatrix';
import { GridViewSwitch } from './GridViewSwitch';
import { GridDayView } from './GridDayView';
import { GridMatchCardView } from './GridMatchCard';
import { GridHiddenTimesNotice } from './GridHiddenTimesNotice';
import { useGridTimeRows } from './useGridTimeRows';

/** A match whose tournament isn't in the grid's list — shouldn't happen, but don't render blank. */
const UNKNOWN_TOURNAMENT: TournamentBadge = { name: 'Otro torneo', colorIndex: 0 };

// Mouse and touch are split into two sensors instead of a single `PointerSensor` because they
// need opposite activation constraints, and `PointerSensor` can only carry one for both.
//
// Mouse: a minimum distance, so a plain click on the card's pencil button (or the "Editar"
// text) doesn't register as the start of a drag.
//
// Touch: a delay instead. On a finger, a distance constraint competes with page scrolling —
// every swipe that starts on a card is ambiguous, and the browser wins by claiming the gesture
// as a pan, which cancels the drag. That is why dragging did nothing at all on mobile. With a
// delay, dnd-kit cancels the activation itself when the finger travels more than `tolerance`
// before the timer fires (so a quick swipe still scrolls the grid), and once the drag is active
// it calls `preventDefault` on every move (so the page stops scrolling under it).
//
// This is also why the cards must NOT get `touch-action: none`, the intuitive fix for a drag
// the browser keeps stealing: the cards cover most of each cell, so it would kill vertical
// scrolling on a phone. The delay resolves the conflict without giving that up.
//
// Both objects are module-level so they stay referentially stable and `useSensor` can memoize.
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 6 } };
const TOUCH_SENSOR_OPTIONS = { activationConstraint: { delay: 250, tolerance: 8 } };

// dnd-kit auto-scrolls whichever scrollable ancestor the pointer is hovering near the edge of, and
// its defaults are built for a desktop window: a band of 20% of the container on each side, and
// `acceleration: 10` applied every 5ms tick — about 2000px per second at the deepest point of the
// band. On a phone that made the page bolt to the bottom the moment a card was picked up: the
// bottom band is ~140px of a ~700px viewport, which is exactly where a thumb holds a card, and the
// scroll only needs one pixel of downward travel to unlock (the direction intent is sticky for the
// rest of the drag).
//
// Tuned rather than turned off: the grid is taller and wider than a phone screen, so dragging a
// match to a slot that is off-screen depends on this. The vertical band is halved so a card has to
// be pushed deliberately towards the edge, and the speed drops to ~400px/s, which a finger can
// still follow. The horizontal band stays at the default — that one scrolls the grid's own
// `overflow-x-auto` container, which is how a match reaches another day in the weekly view.
const AUTO_SCROLL_OPTIONS = {
  acceleration: 2,
  threshold: { x: 0.2, y: 0.1 },
};

/** The overlay preview is not editable; a stable handler keeps its memoized card from re-rendering. */
const noop = () => {};

interface SharedGridViewProps {
  grid: SharedGrid;
  activeClubId: string;
  onActiveClubIdChange: (clubId: string) => void;
  /** matchId → `YYYY-MM-DDTHH:mm:ss` local, not yet saved. */
  moves: Map<string, string>;
  onEditMatch: (match: GridMatch) => void;
  /** matchId -> qualifier labels for its undecided bracket slots. Empty when there are none. */
  placeholderLabels: PlaceholderLabelMap;
  /** A match got dropped on a different cell. Task 5 wires this into the pending-changes state. */
  onMove: (matchId: string, date: string, time: string) => void;
  /** Opens the "who can play" list for a slot. */
  onShowAvailableTeams: (slot: GridSlotRef) => void;
}

export function SharedGridView({
  grid,
  activeClubId,
  onActiveClubIdChange,
  moves,
  onEditMatch,
  placeholderLabels,
  onMove,
  onShowAvailableTeams,
}: SharedGridViewProps) {
  // Rows added or taken out by hand. Not part of the page's `moves` draft: see `useGridTimeRows`.
  const { extraTimes, hiddenTimes, addTime, removeTime, showHiddenTimes, unhideTimes, resetTimes } =
    useGridTimeRows();

  // Reset instead of scoping per club (unlike `moves`, which locks the club selector, and
  // `selectedDate`, which is revalidated against `model.dates` below): an empty row added by
  // hand has nothing worth carrying across clubs, and a row that already holds a match doesn't
  // need `extraTimes` to survive anyway — the match itself puts that time back on the axis via
  // `buildGrid`, on this club and on reload. The same goes for `hiddenTimes`.
  useEffect(() => resetTimes(), [activeClubId, resetTimes]);

  const [viewMode, setViewMode] = useState<GridViewMode>('week');
  const [selectedDate, setSelectedDate] = useState('');

  const model = useMemo(
    () => buildGrid(grid, activeClubId, moves, extraTimes, hiddenTimes),
    [grid, activeClubId, moves, extraTimes, hiddenTimes],
  );

  // A hidden time that a match sits on is back on screen (`buildGrid` never hides a match), so the
  // hide is forgotten: otherwise the row would vanish again the moment that match moves away — a
  // discarded draft or a saved edit that put a match back on it, then a drag that takes it off.
  useEffect(() => unhideTimes(model.times), [model.times, unhideTimes]);

  const badges = useMemo(() => {
    const map = new Map<string, TournamentBadge>();
    grid.tournaments.forEach((t, i) => map.set(t.id, { name: t.name, colorIndex: i }));
    return map;
  }, [grid.tournaments]);

  const badgeOf = useCallback(
    (id: string) => badges.get(id) ?? UNKNOWN_TOURNAMENT,
    [badges],
  );

  const tournamentOrder = useMemo(
    () => tournamentOrderOf(grid.tournaments),
    [grid.tournaments],
  );

  const pendingMatchIds = useMemo(() => new Set(moves.keys()), [moves]);
  const hasPendingChanges = moves.size > 0;

  // Keeps the selected day valid: `dates` changes when the club changes or the grid refetches,
  // and a day that no longer exists would render an empty view with no way back.
  const activeDate =
    selectedDate && model.dates.includes(selectedDate) ? selectedDate : (model.dates[0] ?? '');

  const [dragging, setDragging] = useState<GridMatch | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor),
  );

  // Resolved once instead of per JSX slot: the overlay needs the same effective cell and badge
  // the real card uses.
  const dragPreview = dragging
    ? {
        match: dragging,
        badge: badgeOf(dragging.tournamentId),
        ...effectiveCell(dragging, moves),
      }
    : null;

  const handleDragStart = (event: DragStartEvent) => {
    setDragging((event.active.data.current as { match: GridMatch }).match);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const match = dragging;
    setDragging(null);
    if (!match || !event.over) return;

    // Every intersection has a cell now, so `event.over` always resolves; the guard stays as a
    // defensive narrow, not as a real branch.
    const cell = model.cells.get(String(event.over.id));
    if (!cell) return;

    // Dropping where it already sits isn't a change: the backend also rejects moving a match
    // to the time it's already at ("El partido ya se juega a la hora enviada").
    const current = effectiveCell(match, moves);
    if (current.date === cell.date && current.time === cell.startTime) return;

    onMove(match.matchId, cell.date, cell.startTime);
  };

  return (
    <DndContext
      sensors={sensors}
      autoScroll={AUTO_SCROLL_OPTIONS}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="space-y-4">
        {grid.clubs.length > 1 && (
          // Disabled while there's a pending move: `moves` is one Map for the whole page, not
          // scoped per club, so a draft made on another club stays invisible after switching —
          // and "Descartar" would wipe it too, without the organizer ever seeing it. Locking the
          // selector until the draft is saved or discarded is cheaper and more honest than
          // scoping the draft per club.
          <Select
            value={activeClubId}
            onValueChange={onActiveClubIdChange}
            disabled={hasPendingChanges}
          >
            <SelectTrigger
              className="w-[240px]"
              title={
                hasPendingChanges
                  ? 'Guardá o descartá los cambios antes de cambiar de club.'
                  : undefined
              }
            >
              <SelectValue placeholder="Elegí un club" />
            </SelectTrigger>
            <SelectContent>
              {grid.clubs.map((club) => (
                <SelectItem key={club.id} value={club.id}>
                  {club.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          {grid.tournaments.map((t, i) => (
            <span key={t.id} className="flex items-center gap-1.5">
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  TOURNAMENT_DOT_COLORS[i % TOURNAMENT_DOT_COLORS.length],
                )}
                aria-hidden
              />
              <span className={cn(t.current && 'font-semibold')}>
                {t.name}
                {t.current && ' (actual)'}
              </span>
            </span>
          ))}
        </div>

        <GridViewSwitch
          mode={viewMode}
          onModeChange={setViewMode}
          dates={model.dates}
          selectedDate={activeDate}
          onSelectedDateChange={setSelectedDate}
        />

        {/* Outside both views on purpose: the day view has no X to hide a row and no "Agregar
            horario" row, so this is the only way back there. */}
        <GridHiddenTimesNotice times={model.hiddenTimes} onShow={showHiddenTimes} />

        {/* Press-and-hold to drag is undiscoverable, and on a touch screen it is the only way
            to move a match (see `TOUCH_SENSOR_OPTIONS`). Shown only where it applies. */}
        <p className="text-xs text-gray-500 sm:hidden">
          Mantené apretado un partido para moverlo. Si la hora que buscás no está en la grilla,
          agregala abajo y arrastrá el partido ahí.
        </p>

        {viewMode === 'week' ? (
          <GridMatrix
            model={model}
            badgeOf={badgeOf}
            pendingMatchIds={pendingMatchIds}
            editDisabled={hasPendingChanges}
            onEditMatch={onEditMatch}
            placeholderLabels={placeholderLabels}
            dragging={dragging}
            onAddTime={addTime}
            onRemoveTime={removeTime}
            onShowAvailableTeams={onShowAvailableTeams}
          />
        ) : (
          <GridDayView
            model={model}
            date={activeDate}
            tournamentOrder={tournamentOrder}
            badgeOf={badgeOf}
            pendingMatchIds={pendingMatchIds}
            editDisabled={hasPendingChanges}
            onEditMatch={onEditMatch}
            placeholderLabels={placeholderLabels}
            dragging={dragging}
            onShowAvailableTeams={onShowAvailableTeams}
          />
        )}
      </div>

      {/* The overlay preview must NOT be the draggable wrapper: see the comment on
          `GridMatchCardView` in `GridMatchCard.tsx` for why reusing the same draggable id here
          would break the next drag on this match. */}
      <DragOverlay>
        {dragPreview && (
          <GridMatchCardView
            match={dragPreview.match}
            tournamentName={dragPreview.badge.name}
            colorIndex={dragPreview.badge.colorIndex}
            date={dragPreview.date}
            time={dragPreview.time}
            placeholders={placeholderLabels.get(dragPreview.match.matchId)}
            onEdit={noop}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
